import React, { useState, useEffect, useRef } from "react";
import { BiArrowBack, BiUser, BiSearch } from "react-icons/bi";
import { BsBicycle, BsTruck } from "react-icons/bs";
import { FaEye } from "react-icons/fa6";
import { MdLocationOn, MdMoreVert } from "react-icons/md";
import { HiOutlineDotsVertical } from "react-icons/hi";
import { useNavigate } from "react-router-dom";
import { LiveOrder, TrackingStats, Order } from "../../types/orders";
import OrderDetailsModal from "../../components/modals/OrderDetailsModal";
import OrderTrackingInterface from "../../components/OrderTrackingInterface";
import ReassignOrderModal from "../../components/modals/ReassignOrderModal";
import toast from "react-hot-toast";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  getAvailableRiders,
  getRiderStatsOverview,
  getRealTimeStats,
} from "../../redux/thunk";

interface Rider {
  id: string;
  name: string;
  location: string;
  rating: number;
  totalOrders: number;
  availability: "Available" | "Busy";
  vehicleType: "bicycle" | "motorcycle";
  distanceToPickup: string;
  riderType: "Internal" | "External";
}

interface OrderTableData {
  id: string;
  orderNumber: string;
  rider: string;
  lastUpdate: string;
  status: string;
  actions: string[];
}

const ActionDropdown: React.FC<{
  onReassign: () => void;
  onTrack: () => void;
  onViewDetails: () => void;
}> = ({ onReassign, onTrack, onViewDetails }) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isOpen]);

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="p-2 text-gray-400 hover:text-gray-600 transition-colors rounded-lg hover:bg-gray-50"
        aria-label="Actions"
      >
        <HiOutlineDotsVertical className="w-5 h-5" />
      </button>

      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute right-0 top-full mt-2 bg-white border border-gray-200 rounded-lg shadow-xl z-[9999] min-w-[140px] py-2"
          style={{
            boxShadow:
              "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
          }}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              onReassign();
              setIsOpen(false);
            }}
            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center"
          >
            Reassign
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onTrack();
              setIsOpen(false);
            }}
            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center"
          >
            Track
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onViewDetails();
              setIsOpen(false);
            }}
            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center"
          >
            View Details
          </button>
        </div>
      )}
    </div>
  );
};

const RealTimeTracking: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All Statuses");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Get data from Redux store
  const {
    availableRiders,
    riderStatsOverview,
    realTimeStats,
    realTimeStatsPagination,
    realTimeTrackingStatus,
    error,
  } = useAppSelector((state) => state.logistics);

  // Fetch data on component mount
  useEffect(() => {
    dispatch(getAvailableRiders());
    dispatch(getRiderStatsOverview());
    dispatch(getRealTimeStats({ page: currentPage, limit: itemsPerPage }));
  }, [dispatch, currentPage, itemsPerPage]);

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleReassignOrder = async (orderData: OrderTableData) => {
    setIsLoading(true);

    try {
      // Create a mock detailed order for the reassign modal
      const detailedOrder: Order = {
        id: orderData.id,
        orderNumber: orderData.orderNumber.split(":")[0], // Extract ORD-XXX from "ORD-243: Sarah Willy"
        customer: {
          id: "1",
          name: orderData.orderNumber.split(": ")[1] || "Sarah Willy", // Extract customer name
          phone: "+234 567 867 9089",
          address: "12 Avenue Close, Gbagdada",
        },
        restaurant: {
          id: "1",
          name: "Peteri Amadu",
          phone: "+234 567 867 9089",
          address: "12 Avenue Close, Gbagdada",
        },
        rider: {
          id: "1",
          name: orderData.rider,
          phone: "+234 567 867 9089",
          vehicleType: "bicycle",
          rating: 4.5,
        },
        items: [
          { id: "1", name: "Jollof Rice", quantity: 2, price: 1500 },
          { id: "2", name: "Pepsi Pet", quantity: 1, price: 400 },
          { id: "3", name: "Beef", quantity: 3, price: 1500 },
        ],
        status: orderData.status.toLowerCase().replace(" ", "_") as any,
        priority: "normal",
        total: 3400,
        estimatedDeliveryTime: "15 Minutes",
        deliveryRoute: {
          pickupLocation: {
            name: "The Place Restaurant",
            address: "No 4 Estaport, Avenue, Gbagada (The Place Restaurant)",
          },
          deliveryLocation: {
            name: "Home Sweet Home",
            address: "12 Avenue Close, Gbagdada",
          },
        },
        timeline: [
          {
            status: "pending",
            timestamp: "02:20pm",
            description: "Order received by vendor",
            completed: true,
          },
          {
            status: "confirmed",
            timestamp: "02:20pm",
            description: "Vendor accepted the order",
            completed: true,
          },
          {
            status: "preparing",
            timestamp: "02:20pm",
            description: "Food is being prepared",
            completed: true,
          },
          {
            status: "ready",
            timestamp: "02:20pm",
            description: "Order ready for pickup",
            completed: true,
          },
          {
            status: "picked_up",
            timestamp: "02:20pm",
            description: "Rider picked up the order",
            completed: true,
          },
          {
            status: "out_for_delivery",
            timestamp: "",
            description: "On the way to customer",
            completed: false,
          },
          {
            status: "delivered",
            timestamp: "",
            description: "Order delivered to customer",
            completed: false,
          },
        ],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      setSelectedOrder(detailedOrder);
      setShowReassignModal(true);
    } catch (error) {
      toast.error("Failed to load order details for reassignment");
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewOrder = async (orderData: OrderTableData) => {
    setIsLoading(true);

    try {
      // Create a mock detailed order for the modal
      const detailedOrder: Order = {
        id: orderData.id,
        orderNumber: orderData.orderNumber.split(":")[0],
        customer: {
          id: "1",
          name: orderData.orderNumber.split(": ")[1] || "Sarah Willy",
          phone: "+234 567 867 9089",
          address: "12 Avenue Close, Gbagdada",
        },
        restaurant: {
          id: "1",
          name: "Peteri Amadu",
          phone: "+234 567 867 9089",
          address: "12 Avenue Close, Gbagdada",
        },
        rider: {
          id: "1",
          name: orderData.rider,
          phone: "+234 567 867 9089",
          vehicleType: "bicycle",
          rating: 4.5,
        },
        items: [
          { id: "1", name: "Jollof Rice", quantity: 2, price: 1500 },
          { id: "2", name: "Pepsi Pet", quantity: 1, price: 400 },
          { id: "3", name: "Beef", quantity: 3, price: 1500 },
        ],
        status: orderData.status.toLowerCase().replace(" ", "_") as any,
        priority: "normal",
        total: 3400,
        estimatedDeliveryTime: "15 Minutes",
        deliveryRoute: {
          pickupLocation: {
            name: "The Place Restaurant",
            address: "No 4 Estaport, Avenue, Gbagada (The Place Restaurant)",
          },
          deliveryLocation: {
            name: "Home Sweet Home",
            address: "12 Avenue Close, Gbagdada",
          },
        },
        timeline: [
          {
            status: "pending",
            timestamp: "02:20pm",
            description: "Order received by vendor",
            completed: true,
          },
          {
            status: "confirmed",
            timestamp: "02:20pm",
            description: "Vendor accepted the order",
            completed: true,
          },
          {
            status: "preparing",
            timestamp: "02:20pm",
            description: "Food is being prepared",
            completed: true,
          },
          {
            status: "ready",
            timestamp: "02:20pm",
            description: "Order ready for pickup",
            completed: true,
          },
          {
            status: "picked_up",
            timestamp: "02:20pm",
            description: "Rider picked up the order",
            completed: true,
          },
          {
            status: "out_for_delivery",
            timestamp: "",
            description: "On the way to customer",
            completed: false,
          },
          {
            status: "delivered",
            timestamp: "",
            description: "Order delivered to customer",
            completed: false,
          },
        ],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      setSelectedOrder(detailedOrder);
      setShowOrderModal(true);
    } catch (error) {
      toast.error("Failed to load order details");
    } finally {
      setIsLoading(false);
    }
  };

  const handleTrackOrder = async (orderData: OrderTableData) => {
    setIsLoading(true);

    try {
      // Create a mock detailed order for tracking
      const detailedOrder: Order = {
        id: orderData.id,
        orderNumber: orderData.orderNumber.split(":")[0],
        customer: {
          id: "1",
          name: orderData.orderNumber.split(": ")[1] || "Sarah Willy",
          phone: "+234 567 867 9089",
          address: "12 Avenue Close, Gbagdada",
        },
        restaurant: {
          id: "1",
          name: "Peteri Amadu",
          phone: "+234 567 867 9089",
          address: "12 Avenue Close, Gbagdada",
        },
        rider: {
          id: "1",
          name: orderData.rider,
          phone: "+234 567 867 9089",
          vehicleType: "bicycle",
          rating: 4.5,
        },
        items: [
          { id: "1", name: "Jollof Rice", quantity: 2, price: 1500 },
          { id: "2", name: "Pepsi Pet", quantity: 1, price: 400 },
          { id: "3", name: "Beef", quantity: 3, price: 1500 },
        ],
        status: orderData.status.toLowerCase().replace(" ", "_") as any,
        priority: "normal",
        total: 3400,
        estimatedDeliveryTime: "15 Minutes",
        deliveryRoute: {
          pickupLocation: {
            name: "The Place Restaurant",
            address: "No 4 Estaport, Avenue, Gbagada (The Place Restaurant)",
          },
          deliveryLocation: {
            name: "Home Sweet Home",
            address: "12 Avenue Close, Gbagdada",
          },
        },
        timeline: [
          {
            status: "pending",
            timestamp: "02:20pm",
            description: "Order received by vendor",
            completed: true,
          },
          {
            status: "confirmed",
            timestamp: "02:20pm",
            description: "Vendor accepted the order",
            completed: true,
          },
          {
            status: "preparing",
            timestamp: "02:20pm",
            description: "Food is being prepared",
            completed: true,
          },
          {
            status: "ready",
            timestamp: "02:20pm",
            description: "Order ready for pickup",
            completed: true,
          },
          {
            status: "picked_up",
            timestamp: "02:20pm",
            description: "Rider picked up the order",
            completed: true,
          },
          {
            status: "out_for_delivery",
            timestamp: "",
            description: "On the way to customer",
            completed: false,
          },
          {
            status: "delivered",
            timestamp: "",
            description: "Order delivered to customer",
            completed: false,
          },
        ],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      setSelectedOrder(detailedOrder);
      setShowTrackingModal(true);
    } catch (error) {
      toast.error("Failed to load tracking information");
    } finally {
      setIsLoading(false);
    }
  };

  const handleContactCustomer = (orderId: string) => {
    toast.success("Contacting customer...");
    // Implement contact customer logic
  };

  const handleContactRestaurant = (orderId: string) => {
    toast.success("Contacting restaurant...");
    // Implement contact restaurant logic
  };

  const handleReassignRider = (orderId: string) => {
    toast.success("Opening rider reassignment...");
    // Implement reassign rider logic
  };

  const handleEscalateIssue = (orderId: string) => {
    toast.success("Escalating issue...");
    // Implement escalate issue logic
  };

  const handleConfirmReassignment = async (riderId: string, reason: string) => {
    try {
      // Implement reassignment logic here
      toast.success(`Order successfully reassigned. Reason: ${reason}`);
      setShowReassignModal(false);
      setSelectedOrder(null);
    } catch (error) {
      toast.error("Failed to reassign order");
      throw error;
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status.toLowerCase()) {
      case "delivering":
        return "bg-blue-100 text-blue-800";
      case "enroute":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="flex-1 flex flex-col bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/dashboard/logistics")}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <BiArrowBack className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-semibold text-gray-900">
                  Logistics
                </h1>
                <span className="text-gray-400">{">"}</span>
                <span className="text-lg text-gray-600">
                  Real Time Tracking
                </span>
              </div>
              <p className="text-sm text-gray-600 mt-1">
                Monitor vendor earnings, process payouts, and manage financial
                adjustments.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">
                  Available Riders
                </p>
                <p className="text-3xl font-semibold text-gray-900">
                  {realTimeTrackingStatus === "loading"
                    ? "..."
                    : riderStatsOverview?.availabeRiders || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Busy Riders</p>
                <p className="text-3xl font-semibold text-gray-900">
                  {realTimeTrackingStatus === "loading"
                    ? "..."
                    : riderStatsOverview?.busyRiders || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">
                  Active Orders
                </p>
                <p className="text-3xl font-semibold text-gray-900">
                  {realTimeTrackingStatus === "loading"
                    ? "..."
                    : riderStatsOverview?.activeOrders || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">
                  Reassignments Today
                </p>
                <p className="text-3xl font-semibold text-gray-900">
                  {realTimeTrackingStatus === "loading"
                    ? "..."
                    : riderStatsOverview?.reassignedOrders || 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Available Riders Section */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <BiUser className="w-5 h-5 text-teal-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              Available Riders ({availableRiders.length})
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {realTimeTrackingStatus === "loading" ? (
              <div className="col-span-3 text-center py-8">
                <p className="text-gray-500">Loading available riders...</p>
              </div>
            ) : availableRiders.length === 0 ? (
              <div className="col-span-3 text-center py-8">
                <p className="text-gray-500">
                  No available riders at the moment
                </p>
              </div>
            ) : (
              availableRiders.map((rider) => (
                <div
                  key={rider._id}
                  className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                        <BiUser className="w-5 h-5 text-gray-600" />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">
                          {rider.fullName}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {rider.riderInfo?.homeAddress || rider.phoneNumber}
                        </p>
                      </div>
                    </div>
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        rider.riderType === "Internal"
                          ? "bg-green-100 text-green-800"
                          : "bg-yellow-100 text-yellow-800"
                      }`}
                    >
                      {rider.riderType}
                    </span>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-1">
                        <span className="text-yellow-500">★</span>
                        <span>{rider.averageRating || "N/A"}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <BsBicycle className="w-3 h-3" />
                        <span>{rider.riderInfo?.model || "Vehicle"}</span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="flex justify-between items-center text-sm">
                      <div>
                        <span className="font-medium text-gray-900">Phone</span>
                        <p className="text-sm text-gray-900">
                          {rider.phoneNumber}
                        </p>
                      </div>
                      <div className="text-right">
                        <span className="text-gray-600">
                          {rider.totalRatings} Ratings
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Orders Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Orders</h2>
            <div className="flex items-center gap-4">
              <div className="relative">
                <BiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search Riders"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
              >
                <option>All Statuses</option>
                <option>Delivering</option>
                <option>Enroute</option>
              </select>
            </div>
          </div>

          <div className="overflow-x-auto relative">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Order Number
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rider
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Update
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th> */}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {realTimeTrackingStatus === "loading" ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-6 py-8 text-center text-gray-500"
                    >
                      Loading orders...
                    </td>
                  </tr>
                ) : realTimeStats.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-6 py-8 text-center text-gray-500"
                    >
                      No orders at the moment
                    </td>
                  </tr>
                ) : (
                  realTimeStats.map((order) => {
                    const orderData: OrderTableData = {
                      id: order.orderId,
                      orderNumber: order.orderNumber,
                      rider: order.riderName,
                      lastUpdate: order.lastUpdate,
                      status: order.orderStatus,
                      actions: ["view", "track", "reassign"],
                    };
                    return (
                      <tr key={order.orderId} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {order.orderNumber}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {order.riderName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {order.lastUpdate}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeClass(
                              order.orderStatus
                            )}`}
                          >
                            {order.orderStatus}
                          </span>
                        </td>
                        {/* <td className="px-6 py-4 whitespace-nowrap text-right relative">
                          <div className="flex justify-end">
                            <ActionDropdown
                              onViewDetails={() => handleViewOrder(orderData)}
                              onTrack={() => handleTrackOrder(orderData)}
                              onReassign={() => handleReassignOrder(orderData)}
                            />
                          </div>
                        </td> */}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>

            {/* Pagination */}
            {realTimeStatsPagination &&
              realTimeStatsPagination.totalPages > 1 && (
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    Showing page {realTimeStatsPagination.page} of{" "}
                    {realTimeStatsPagination.totalPages} (
                    {realTimeStatsPagination.totalRiders} total orders)
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={
                        currentPage === realTimeStatsPagination.totalPages
                      }
                      className="px-3 py-1 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
          </div>
        </div>
      </div>

      {/* Order Details Modal */}
      {selectedOrder && (
        <OrderDetailsModal
          order={selectedOrder}
          isOpen={showOrderModal}
          onClose={() => {
            setShowOrderModal(false);
            setSelectedOrder(null);
          }}
          onContactCustomer={handleContactCustomer}
          onContactRestaurant={handleContactRestaurant}
          onReassignRider={handleReassignRider}
          onEscalateIssue={handleEscalateIssue}
        />
      )}

      {/* Order Tracking Modal */}
      {selectedOrder && showTrackingModal && (
        <div className="fixed inset-0 z-50 bg-white">
          <OrderTrackingInterface
            order={selectedOrder}
            onClose={() => {
              setShowTrackingModal(false);
              setSelectedOrder(null);
            }}
          />
        </div>
      )}

      {/* Reassign Order Modal */}
      {selectedOrder && showReassignModal && (
        <ReassignOrderModal
          order={selectedOrder}
          isOpen={showReassignModal}
          onClose={() => {
            setShowReassignModal(false);
            setSelectedOrder(null);
          }}
          onConfirm={handleConfirmReassignment}
        />
      )}
    </div>
  );
};

export default RealTimeTracking;
